import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap, catchError } from 'rxjs/operators';
import { PieChartComponent } from '../../../shared/reusables/pie-chart/pie-chart.component';
import { BarChartComponent } from '../../../shared/reusables/bar-chart/bar-chart.component';
import { AlertComponent } from '../../../shared/reusables/alert/alert.component';
import { ToastService } from '../../../shared/services/toast.service';
import { AuthService } from '../../../shared/services';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { TooltipComponent } from '../../../shared/tooltip/tooltip.component';
import { AreaQuantityInputComponent } from '../../../shared/area-quantity-input/area-quantity-input.component';
import { ExcelService } from '../../../shared/services/excel.service';
import * as bootstrap from 'bootstrap';
import { InventoryDataService, ItemTypeDTO } from '../../../../shared/services/inventory-data.service';
import { InventoryImportService, ImportContext } from '../../../../shared/services/inventory-import.service';
import { InventoryItemModalComponent } from '../inventory-item-modal/inventory-item-modal.component';
import { MobileInventoryViewComponent } from '../mobile-inventory-view/mobile-inventory-view.component';
import {
  CategoryDTO,
  SupplierDTO,
  SupplierLocationDTO,
  UnitOfMeasureDTO,
  EnhancedItemDTO,
  ItemDTO,
  InventoryTransactionDTO,
  ItemSupplierPriceHistoryDTO,
} from '../inventory-dashboard.component';

type SortableInventoryKeys = keyof EnhancedItemDTO | 'CategoryName' | 'purchaseUOMID' | 'purchasePrice' | 'sellingPrice' | 'itemCode' | 'itemName' | 'unitCost';

/**
 * Raw Materials page — split out of InventoryDashboardComponent (W5 step 4/5).
 * Owns only the raw-material half of the old monolith's state: its own
 * rawMaterials/filteredRawMaterials, its own search/pagination/sort, its own
 * filter-panel state (including an independent selectedCategoryFilter — the
 * old component shared one selectedCategoryFilter between both panels, a
 * latent bug fixed simply by these being separate classes now), its own
 * add/edit modal state, and its own charts/statistics computed only from its
 * raw-material item set.
 *
 * Still fetches via InventoryDataService.loadItems() (the full item list,
 * filtered client-side to raw materials in ngOnInit) — a later pass swaps
 * this for a raw-materials-only fetch.
 */
@Component({
  selector: 'app-inventory-raw-materials',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PieChartComponent,
    BarChartComponent,
    AlertComponent,
    TooltipComponent,
    AreaQuantityInputComponent,
    InventoryItemModalComponent,
    MobileInventoryViewComponent,
  ],
  templateUrl: './inventory-raw-materials.component.html',
  styleUrls: [
    '../../shared/offcanvas-sheet/offcanvas-sheet.shared.css',
    '../inventory-dashboard.component.css',
  ],
})
export class InventoryRawMaterialsComponent implements OnInit {
  readonly String = String;

  // Data
  rawMaterials: EnhancedItemDTO[] = [];
  originalRawMaterials: EnhancedItemDTO[] = [];
  filteredRawMaterials: EnhancedItemDTO[] = [];
  categories: CategoryDTO[] = [];
  suppliers: SupplierDTO[] = [];
  uomList: UnitOfMeasureDTO[] = [];
  itemTypes: ItemTypeDTO[] = [];

  // Stats
  totalRawMaterials = 0;
  lowStockItems = 0;
  totalRawMaterialValue = 0;
  lowStockItemsList: any[] = [];
  showLowStockPopup: boolean | null = null;

  // Charts
  categoryLabels: string[] = [];
  categoryData: number[] = [];
  stackedBarData: {
    labels: string[];
    datasets: { label: string; data: number[]; backgroundColor: string[]; borderRadius: number; borderWidth: number }[];
  } | null = null;
  baseColors = [
    '#007A8F', '#6C7A3E', '#B0213C', '#26284F', '#7FBF7F',
    '#F3D250', '#2C6B47', '#A0D83A', '#3C9C8E', '#FF6F61', '#FF2E17',
  ];
  ItemsPerPage = 10;
  currentPage = 1;
  totalPages = 1;
  totalItemsCount = 0;
  goToPage = (page: number) => this.onPageChange(page);
  onPageSizeChange = (size: number) => {
    this.ItemsPerPage = Number(size) > 0 ? Number(size) : 10;
    this.currentPage = 1;
    this.updateChartData(1);
  };

  // Sort / pagination (table)
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  rawMaterialsCurrentPage = 1;
  rawMaterialsPageSize = 15;

  // Search / filters — independent of the Products page's filter state
  rawMaterialsSearchTerm = '';
  selectedCategoryFilter: number | null = null;
  selectedUOMFilter: number | null = null;
  showLowStockOnly = false;
  showNonLowStockOnly = false;
  rawTypeFilter: 'all' | 'standard' | 'buyout' = 'all';
  isFilterPanelOpenRawMaterials = false;

  // Add/edit modal state
  isEditing = false;
  isFinishedGood = false; // always false on this page — kept so the shared modal input has a value
  isServiceItem = false;
  isAddingQuantity = false;
  isUpdatingPurchasePrice = false;
  showPurchaseFields = false;
  showAddModal = false;
  showAddOptionModal = false;
  showItemSelectModal = false;
  showDeleteModal = false;
  animateClickRaw = false;
  selectedItem: EnhancedItemDTO | null = null;
  selectedSupplier: SupplierDTO | null = null;
  selectedCategory: CategoryDTO | null = null;
  isCreatingSupplier = false;
  isCreatingCategory = false;

  supplierForm: FormGroup;
  inventoryForm: FormGroup;
  categoryForm: FormGroup;

  // Alert
  successMessage: string | null = null;
  errorMessage: string | null = null;
  isAlertVisible = false;
  alertTitle = '';
  alertMessage = '';
  alertBgColor = 'bg-danger';
  alerttextcolor = '#ffffff';

  userRole: string | null = null;
  tenantName: any;
  fullName: any;
  isLoading = true;

  // Reorder level prompt for items with threshold = 0
  showReorderPrompt = false;
  zeroReorderItems: EnhancedItemDTO[] = [];
  reorderEditValues: { [itemID: number]: number } = {};

  // Import/Export
  isImporting = false;
  importProgress = 0;
  importResults: { success: number; errors: any[] } = { success: 0, errors: [] };
  @ViewChild('fileInput') fileInput!: ElementRef;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    public authService: AuthService,
    private excelService: ExcelService,
    private router: Router,
    private toast: ToastService,
    private inventoryDataService: InventoryDataService,
    private inventoryImportService: InventoryImportService,
  ) {
    this.supplierForm = this.fb.group({
      companyName: ['', Validators.required],
      primaryContactPerson: ['', Validators.required],
      primaryPhone: ['', Validators.required],
      primaryEmail: ['', [Validators.required, Validators.email]],
      website: [''],
      branchName: ['', Validators.required],
      addressLine1: ['', Validators.required],
      addressLine2: [''],
      city: ['', Validators.required],
      region: [''],
      postalCode: ['', Validators.required],
      country: ['', Validators.required],
      locationContactPerson: ['', Validators.required],
      locationEmail: ['', [Validators.required, Validators.email]],
      locationPhone: ['', Validators.required],
    });

    this.inventoryForm = this.fb.group({
      itemName: ['', Validators.required],
      itemCode: [''],
      PurchaseUOMID: [null, [Validators.required]],
      quantityToAdd: [0, [Validators.required, Validators.min(0)]],
      currentQuantity: [0],
      lowStockThreshold: [null, [Validators.required, Validators.min(0)]],
      purchasePrice: [0, [Validators.required, Validators.min(0)]],
      unitCost: [0],
      profitMarginPercent: [0, [Validators.min(0), Validators.max(100)]],
      sellingPrice: [0, [Validators.min(0)]],
      valuationMethodCode: ['FIFO'],
      categoryID: [null, Validators.required],
      itemTypeID: [1, Validators.required],
      supplierID: [null],
      isActive: [true],
      isBuyout: [false],
    });

    this.categoryForm = this.fb.group({
      categoryName: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    const token = this.authService.getToken && this.authService.getToken();
    if (!token) {
      this.authService.logOut();
      return;
    }
    const decodedToken: any = jwtDecode(token);
    this.tenantName = decodedToken.TenantName;
    this.fullName = decodedToken.FullName;
    this.userRole = this.authService.getUserRole();
    this.loadUnitsOfMeasure();
    this.loadItemTypes();

    this.loadCategories().subscribe();
    this.loadSuppliers().subscribe({
      next: () => this.loadItems(),
      error: () => {
        this.showAlert('Error', 'Failed to load initial data');
      },
    });

    const purchasePriceCtrl = this.inventoryForm.get('purchasePrice');
    const qtyCtrl = this.inventoryForm.get('quantityToAdd');
    purchasePriceCtrl?.valueChanges.subscribe(() => this.recalculateUnitCost());
    qtyCtrl?.valueChanges.subscribe(() => this.recalculateUnitCost());
  }

  private loadUnitsOfMeasure(): void {
    this.inventoryDataService.loadUnitsOfMeasure().subscribe({
      next: (uoms) => { this.uomList = uoms; },
      error: () => {},
    });
  }

  loadCategories(): Observable<CategoryDTO[]> {
    return this.inventoryDataService.loadCategories().pipe(tap((categories) => { this.categories = categories; }));
  }

  loadSuppliers(): Observable<SupplierDTO[]> {
    return this.inventoryDataService.loadSuppliers().pipe(tap((suppliers) => { this.suppliers = suppliers; }));
  }

  loadItemTypes(): void {
    this.inventoryDataService.loadItemTypes().pipe(tap((itemTypes) => { this.itemTypes = itemTypes; })).subscribe();
  }

  get desktopSupplierOptions() {
    return this.suppliers.map(s => ({ value: String(s.supplierID), label: `${s.companyName}${s.primaryEmail ? ' - ' + s.primaryEmail : ''}` }));
  }

  get desktopCategoryOptions() {
    return this.categories.map(c => ({ value: String(c.categoryId), label: c.categoryName }));
  }

  loadItems(): void {
    this.inventoryDataService.loadItemsByType(1).subscribe({
      next: ({ items, transactions }) => {
        const categoryMap = new Map<number, string>();
        this.categories.forEach(category => categoryMap.set(category.categoryId, category.categoryName));

        const transactionsByItem = new Map<number, InventoryTransactionDTO[]>();
        transactions.forEach(tx => {
          if (!transactionsByItem.has(tx.itemID)) transactionsByItem.set(tx.itemID, []);
          transactionsByItem.get(tx.itemID)?.push(tx);
        });

        // GET /api/Items/ByType/1 already filters to itemTypeID === 1 and isActive on the server.
        const rawItems = items;

        const itemsWithQuantities = rawItems.map(item => {
          const itemTransactions = transactionsByItem.get(item.itemID) || [];
          const { totalQuantity, totalCost } = this.calculateItemMetrics(itemTransactions);
          const latestTransaction = itemTransactions
            .filter(tx => tx.costPerUnit !== null && tx.costPerUnit !== undefined)
            .sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime())[0];
          const latestUnitCost = latestTransaction ? latestTransaction.costPerUnit : (item.unitCost || item.purchasePrice || 0);

          return {
            ...item,
            quantity: this.calculateItemQuantity(item.itemID, transactions),
            averageCost: totalQuantity > 0 ? totalCost / totalQuantity : 0,
            unitCost: latestUnitCost,
            CategoryName: categoryMap.get(item.categoryID) || 'Unknown',
          };
        });

        this.rawMaterials = itemsWithQuantities;
        this.originalRawMaterials = [...itemsWithQuantities];
        this.filteredRawMaterials = [...itemsWithQuantities];

        this.loadrawmattotal();
        this.calculateStatistics();
        this.prepareChartsData();
        this.updateChartData(1);
        this.isLoading = false;
      },
      error: () => {
        this.showAlert('Error', 'Failed to load inventory data');
      },
    });
  }

  private calculateItemMetrics(transactions: InventoryTransactionDTO[]): { totalQuantity: number; totalCost: number } {
    return transactions.reduce((acc, tx) => {
      if (tx.transactionType === 'Receipt') {
        acc.totalQuantity += tx.quantityChange;
        acc.totalCost += tx.quantityChange * (tx.costPerUnit || 0);
      }
      return acc;
    }, { totalQuantity: 0, totalCost: 0 });
  }

  calculateItemQuantity(itemID: number, transactions: InventoryTransactionDTO[]): number {
    const itemTransactions = transactions.filter(transaction => transaction.itemID === itemID);
    return itemTransactions.reduce((sum, transaction) => sum + transaction.quantityChange, 0);
  }

  loadrawmattotal(): void {
    const active = this.rawMaterials.filter(item => item.isActive !== false);
    this.totalRawMaterialValue = active.reduce((total, item) => total + (item.quantity || 0) * (item.unitCost || 0), 0);
  }

  calculateStatistics(): void {
    this.lowStockItemsList = this.rawMaterials
      .filter(item => item.lowStockThreshold !== null && item.lowStockThreshold !== undefined && (item.quantity || 0) <= item.lowStockThreshold)
      .map(item => ({ name: item.itemName, quantity: item.quantity || 0, threshold: item.lowStockThreshold || 0 }));

    this.totalRawMaterials = this.rawMaterials.length;
    this.lowStockItems = this.rawMaterials.filter(item => (item.quantity ?? 0) < (item.lowStockThreshold ?? 0)).length;

    if (this.lowStockItems > 0 && !sessionStorage.getItem('showLowStockPopupRawMaterials')) {
      this.showLowStockPopup = true;
      sessionStorage.setItem('showLowStockPopupRawMaterials', 'true');
    }

    this.zeroReorderItems = this.rawMaterials.filter(item => (item.lowStockThreshold == null || item.lowStockThreshold <= 0) && item.isActive !== false);
    if (this.zeroReorderItems.length > 0) {
      this.reorderEditValues = {};
      this.zeroReorderItems.forEach(item => { this.reorderEditValues[item.itemID] = 0; });
      this.showReorderPrompt = true;
    }
  }

  closeLowStockPopup(): void {
    this.showLowStockPopup = false;
  }

  closeReorderPrompt(): void {
    this.showReorderPrompt = false;
  }

  async saveReorderLevels(): Promise<void> {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' });

    for (const item of this.zeroReorderItems) {
      const newThreshold = this.reorderEditValues[item.itemID];
      if (newThreshold != null && newThreshold > 0) {
        const updateDto = { ...item, lowStockThreshold: newThreshold };
        try {
          await this.http.put(`${environment.apiUrl}/api/Items/Update?ItemId=${item.itemID}`, updateDto, { headers }).toPromise();
          item.lowStockThreshold = newThreshold;
        } catch (err) {
          console.error(`Failed to update reorder level for ${item.itemName}:`, err);
        }
      }
    }

    this.showReorderPrompt = false;
    this.zeroReorderItems = this.rawMaterials.filter(item => (item.lowStockThreshold == null || item.lowStockThreshold <= 0) && item.isActive !== false);
    this.showAlert('Success', 'Reorder levels updated successfully', '#4de270', '#ffffff');
  }

  // ── Charts (raw materials only — no more All/Raw/Finished 3-way toggle) ──
  prepareChartsData(): void {
    const categoryMap = new Map<string, number>();
    const categoryQuantityMap = new Map<string, number>();
    const filteredItems = this.rawMaterials.filter(item => item.isActive);

    if (filteredItems.length === 0) {
      this.categoryLabels = [];
      this.categoryData = [];
      this.stackedBarData = null;
      return;
    }

    filteredItems.forEach(item => {
      const categoryName = item.CategoryName || 'Uncategorized';
      const itemQuantity = item.quantity || item.stockLevel || 0;
      const itemValue = itemQuantity * (item.purchasePrice / item.purchaseQuantity || 0);
      categoryMap.set(categoryName, (categoryMap.get(categoryName) || 0) + itemValue);
      categoryQuantityMap.set(categoryName, (categoryQuantityMap.get(categoryName) || 0) + itemQuantity);
    });

    const sortedCategoryEntries = Array.from(categoryMap.entries()).filter(([, value]) => value > 0).sort((a, b) => b[1] - a[1]);
    const categoryLabels = sortedCategoryEntries.map(([key]) => key);
    const categoryValues = sortedCategoryEntries.map(([, value]) => value);

    const sortedQuantityEntries = Array.from(categoryQuantityMap.entries()).filter(([, value]) => value > 0).sort((a, b) => b[1] - a[1]);
    this.categoryLabels = sortedQuantityEntries.map(([key]) => key);
    this.categoryData = sortedQuantityEntries.map(([, value]) => value);

    this.stackedBarData = {
      labels: categoryLabels,
      datasets: [{ label: 'Stock Value', data: categoryValues, backgroundColor: this.baseColors.slice(0, categoryLabels.length), borderRadius: 4, borderWidth: 0 }],
    };
  }

  onPageChange(newPage: number): void {
    this.currentPage = newPage;
    this.updateChartData(newPage);
  }

  updateChartData(page: number = 1): void {
    const filteredItems = this.rawMaterials;

    if (!filteredItems || filteredItems.length === 0) {
      this.totalItemsCount = 0;
      this.totalPages = 1;
      this.currentPage = 1;
      this.stackedBarData = { labels: [], datasets: [{ label: 'Stock Value', data: [], backgroundColor: [], borderRadius: 4, borderWidth: 0 }] };
      return;
    }

    const categoryMap = new Map<string, number>();
    filteredItems.forEach(item => {
      const categoryName = item.CategoryName || 'Uncategorized';
      const itemQuantity = item.quantity || item.stockLevel || 0;
      const itemValue = itemQuantity * (item.purchasePrice / item.purchaseQuantity || 0);
      categoryMap.set(categoryName, (categoryMap.get(categoryName) || 0) + itemValue);
    });

    const sortedEntries = Array.from(categoryMap.entries()).filter(([, value]) => value > 0).sort((a, b) => b[1] - a[1]);
    const allLabels = sortedEntries.map(([key]) => key);
    const allValues = sortedEntries.map(([, value]) => value);

    this.totalItemsCount = allLabels.length;
    const itemsPerPage = Math.max(1, this.ItemsPerPage || 10);
    this.totalPages = Math.max(1, Math.ceil(allLabels.length / itemsPerPage));
    this.currentPage = Math.max(1, Math.min(page, this.totalPages));

    this.stackedBarData = {
      labels: allLabels,
      datasets: [{ label: 'Stock Value', data: allValues, backgroundColor: this.baseColors.slice(0, allLabels.length), borderRadius: 4, borderWidth: 0 }],
    };
  }

  getCategoryColor(categoryName: string): string {
    const index = this.categoryLabels.findIndex(label => label === categoryName);
    if (index !== -1 && index < this.baseColors.length) {
      return this.hexToRgba(this.baseColors[index], 0.7);
    }
    return 'rgba(204, 204, 204, 0.5)';
  }

  hexToRgba(hex: string, alpha: number): string {
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  getUOMName(uomId: number): string {
    if (!uomId) return '';
    const uom = this.uomList.find(u => u.uomid === uomId);
    return uom ? uom.uomCode.replace(/2$/, '²') : '';
  }

  getSelectedUomCode(): string {
    const id = this.inventoryForm.get('PurchaseUOMID')?.value;
    if (!id) return '';
    return this.uomList.find(u => u.uomid === id)?.uomCode ?? '';
  }

  setQuantityToAdd(val: number): void {
    this.inventoryForm.get('quantityToAdd')?.setValue(val);
  }

  compareUOM(uom1: any, uom2: any): boolean {
    const id1 = typeof uom1 === 'object' ? uom1?.uomid : uom1;
    const id2 = typeof uom2 === 'object' ? uom2?.uomid : uom2;
    return id1 === id2;
  }

  private recalculateUnitCost(): void {
    if (this.isEditing || this.isAddingQuantity) return;
    const purchasePrice = Number(this.inventoryForm.get('purchasePrice')?.value) || 0;
    const qty = Number(this.inventoryForm.get('quantityToAdd')?.value) || 0;
    const unitCost = qty > 0 ? parseFloat((purchasePrice / qty).toFixed(2)) : 0;
    this.inventoryForm.get('unitCost')?.setValue(unitCost, { emitEvent: false });
  }

  sortBy(column: SortableInventoryKeys): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    const compareFn = (a: EnhancedItemDTO, b: EnhancedItemDTO) => {
      const valueA = (a as any)[column];
      const valueB = (b as any)[column];
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return this.sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (typeof valueA === 'number' && typeof valueB === 'number') {
        return this.sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
      }
      return 0;
    };

    this.rawMaterials = [...this.rawMaterials].sort(compareFn);
  }

  filterRawMaterials(): void {
    const searchTerm = this.rawMaterialsSearchTerm.toLowerCase().trim();

    this.rawMaterials = this.originalRawMaterials.filter(item => {
      const matchesSearch = !searchTerm ||
        item.itemName?.toLowerCase().includes(searchTerm) ||
        item.itemCode?.toLowerCase().includes(searchTerm) ||
        this.getUOMName(item.purchaseUOMID)?.toLowerCase().includes(searchTerm) ||
        item.CategoryName?.toLowerCase().includes(searchTerm);

      const matchesCategory = !this.selectedCategoryFilter || item.categoryID === this.selectedCategoryFilter;
      const matchesUOM = !this.selectedUOMFilter || item.purchaseUOMID === this.selectedUOMFilter;

      const matchesLowStock = !this.showLowStockOnly ||
        (item.quantity !== undefined && item.lowStockThreshold !== undefined && item.quantity < item.lowStockThreshold);

      const matchesNonLowStock = !this.showNonLowStockOnly ||
        (item.quantity !== undefined && item.lowStockThreshold !== undefined && item.quantity >= item.lowStockThreshold);

      const matchesType = this.rawTypeFilter === 'all' ||
        (this.rawTypeFilter === 'standard' && !item.isBuyout) ||
        (this.rawTypeFilter === 'buyout' && item.isBuyout === true);

      return matchesSearch && matchesCategory && matchesUOM && matchesLowStock && matchesNonLowStock && matchesType;
    });

    this.rawMaterialsCurrentPage = 1;
  }

  resetFilters(): void {
    this.rawMaterialsSearchTerm = '';
    this.selectedCategoryFilter = null;
    this.selectedUOMFilter = null;
    this.showLowStockOnly = false;
    this.showNonLowStockOnly = false;
    this.rawTypeFilter = 'all';
    this.filterRawMaterials();
  }

  toggleFilterPanelRawMaterials(): void {
    this.isFilterPanelOpenRawMaterials = !this.isFilterPanelOpenRawMaterials;
  }

  get rawMaterialsPaged(): EnhancedItemDTO[] {
    const start = (this.rawMaterialsCurrentPage - 1) * this.rawMaterialsPageSize;
    const end = start + this.rawMaterialsPageSize;
    return this.rawMaterials.slice(start, end);
  }

  get rawMaterialsTotalPages(): number {
    const length = this.rawMaterials ? this.rawMaterials.length : 0;
    return Math.max(1, Math.ceil(length / this.rawMaterialsPageSize) || 1);
  }

  get rawMaterialsPages(): number[] {
    const total = this.rawMaterialsTotalPages;
    const maxVisible = 7;
    if (total <= maxVisible) return Array.from({ length: total }, (_, index) => index + 1);

    const half = Math.floor(maxVisible / 2);
    let start = this.rawMaterialsCurrentPage - half;
    let end = this.rawMaterialsCurrentPage + half;
    if (start < 1) { start = 1; end = maxVisible; }
    else if (end > total) { end = total; start = total - maxVisible + 1; }

    const pages: number[] = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  }

  changeRawMaterialsPage(page: number): void {
    const total = this.rawMaterialsTotalPages;
    if (page < 1 || page > total) return;
    this.rawMaterialsCurrentPage = page;
  }

  // ── Add/Edit modal ──────────────────────────────────────────────────────
  handleRawClick(): void {
    this.resetFormStates();
    this.isFinishedGood = false;
    this.isServiceItem = false;
    this.animateClickRaw = true;

    this.inventoryForm.reset({
      itemName: '',
      description: '',
      purchasePrice: 0,
      sellingPrice: 0,
      profitMarginPercent: 0,
      quantityToAdd: 0,
      currentQuantity: 0,
      lowStockThreshold: null,
      valuationMethodCode: 'FIFO',
      itemCode: '',
      PurchaseUOMID: null,
      categoryID: null,
      itemTypeID: 1,
      isActive: true,
      supplierID: null,
    });

    this.showAddModal = true;
    setTimeout(() => { this.animateClickRaw = false; }, 300);
  }

  openAddOption(): void {
    this.resetFormStates();
    this.showAddOptionModal = true;
  }

  selectAddOption(option: 'new' | 'existing'): void {
    if (option === 'new') {
      this.isAddingQuantity = false;
      this.selectedItem = null;
      this.showAddOptionModal = false;
      this.showAddModal = true;
    } else {
      this.showItemSelectModal = true;
    }
  }

  closeItemSelectModal(): void {
    this.showItemSelectModal = false;
  }

  selectItemForQuantityAdd(item: EnhancedItemDTO): void {
    this.showAddOptionModal = false;
    this.selectedItem = item;
    this.isAddingQuantity = true;
    this.isEditing = false;
    this.showAddModal = true;

    this.inventoryForm.reset();
    this.inventoryForm.patchValue({
      itemName: item.itemName,
      quantityToAdd: 1,
      currentQuantity: item.quantity || 0,
      unitCost: item.purchasePrice || 0,
      purchasePrice: item.purchasePrice || 0,
    });

    this.closeItemSelectModal();
  }

  addQuantityToItem(): void {
    const formValue = this.inventoryForm.getRawValue();
    if (!this.selectedItem || !formValue.quantityToAdd || formValue.quantityToAdd <= 0) {
      this.showAlert('Validation Error', 'Invalid quantity');
      return;
    }
    if (!formValue.unitCost || formValue.unitCost <= 0) {
      this.showAlert('Validation Error', 'Please enter a valid unit cost');
      return;
    }

    const currentDate = new Date();
    const transactionData = {
      ItemID: this.selectedItem.itemID,
      WarehouseID: 1,
      TransactionType: 'Receipt',
      QuantityChange: Number(formValue.quantityToAdd),
      CostPerUnit: this.selectedItem.purchasePrice / this.selectedItem.purchaseQuantity,
      TransactionDate: currentDate.toISOString(),
      ReferenceType: 'Manual',
      ReferenceID: `ADD-${currentDate.getTime()}`,
      UserID: 1,
      Notes: 'Quantity added via inventory dashboard',
      BatchNumber: null,
      ExpiryDate: null,
      ReferenceTypeCode: 'MANUAL',
    };
    const token = localStorage.getItem('authToken');
    if (!token) return;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    this.http.post(`${environment.apiUrl}/api/InventoryTransactions/Add`, transactionData, { headers }).subscribe({
      next: () => {
        this.showAlert('Success', 'Quantity added successfully', 'bg-success');
        this.loadItems();
        this.closeAddModal();
      },
      error: () => {
        this.showAlert('Error', 'Failed to add quantity. Please try again.', 'bg-danger');
      },
    });
  }

  editItem(item: EnhancedItemDTO): void {
    this.isEditing = true;
    this.selectedItem = item;
    this.isFinishedGood = false;
    this.isServiceItem = false;
    this.isUpdatingPurchasePrice = false;
    this.showPurchaseFields = false;
    this.showAddModal = true;

    const itemCode = item.itemCode || this.generateSKU(item.itemName);

    this.inventoryForm.reset();
    const formValues: any = {
      itemTypeID: item.itemTypeID,
      itemName: item.itemName,
      itemCode: itemCode,
      sellingPrice: item.sellingPrice || 0,
      PurchaseUOMID: item.purchaseUOMID,
      quantityToAdd: 0,
      currentQuantity: item.quantity || 0,
      lowStockThreshold: item.lowStockThreshold || 0,
      purchasePrice: item.purchasePrice || 0,
      profitMarginPercent: item.profitMarginPercent || 0,
      valuationMethodCode: item.valuationMethodCode || 'FIFO',
      categoryID: item.categoryID,
      supplierID: item.supplierID || null,
      isActive: item.isActive !== false,
      isBuyout: item.isBuyout === true,
    };
    formValues.unitCost = item.unitCost !== undefined ? item.unitCost : item.purchasePrice || 0;
    this.inventoryForm.patchValue(formValues);

    this.selectedCategory = this.categories.find(c => c.categoryId === item.categoryID) || null;
    if (item.supplierID) {
      this.selectedSupplier = this.suppliers.find(s => s.supplierID === item.supplierID) || null;
    }

    this.inventoryForm.markAsUntouched();
    this.inventoryForm.markAsPristine();
  }

  private resetFormStates(): void {
    this.isEditing = false;
    this.selectedItem = null;
    this.selectedSupplier = null;
    this.selectedCategory = null;
    this.isAddingQuantity = false;
    this.isUpdatingPurchasePrice = false;
    this.showPurchaseFields = false;
    this.isCreatingSupplier = false;
    this.isCreatingCategory = false;
    this.isServiceItem = false;

    this.inventoryForm.reset();
    this.supplierForm.reset();
    this.categoryForm.reset();

    this.inventoryForm.patchValue({
      quantityToAdd: 1,
      currentQuantity: 0,
      purchasePrice: 0,
      sellingPrice: 0,
      profitMarginPercent: 0,
      isActive: true,
      itemTypeID: 1,
      valuationMethodCode: 'FIFO',
    });

    this.inventoryForm.markAsUntouched();
    this.inventoryForm.markAsPristine();
    this.supplierForm.markAsUntouched();
    this.supplierForm.markAsPristine();
    this.categoryForm.markAsUntouched();
    this.categoryForm.markAsPristine();
  }

  closeAddModal(): void {
    if (!this.isEditing) {
      this.resetFormStates();
      this.inventoryForm.reset();
    }
    this.showAddModal = false;
    if (!this.isEditing) {
      this.selectedItem = null;
    }
  }

  togglePurchaseFields(): void {
    this.showPurchaseFields = !this.showPurchaseFields;
    if (this.showPurchaseFields && this.selectedItem) {
      this.inventoryForm.patchValue({
        quantityToAdd: this.selectedItem.quantity || 0,
        purchasePrice: this.selectedItem.purchasePrice || 0,
      });
    } else if (!this.showPurchaseFields) {
      this.inventoryForm.patchValue({ purchasePrice: null, quantityToAdd: null });
    }
  }

  onDesktopSupplierSelected(idStr: string | null): void {
    this.selectedSupplier = this.suppliers.find(s => String(s.supplierID) === idStr) ?? null;
  }

  onDesktopCategorySelected(idStr: string | null): void {
    const cat = this.categories.find(c => String(c.categoryId) === idStr) ?? null;
    this.onCategorySelected(cat);
  }

  onCategorySelected(category: CategoryDTO | null): void {
    this.selectedCategory = category;
    if (category) {
      this.inventoryForm.patchValue({ categoryID: category.categoryId });
      this.inventoryForm.get('categoryID')?.markAsTouched();
      this.inventoryForm.get('categoryID')?.markAsDirty();
    } else {
      this.inventoryForm.patchValue({ categoryID: null });
    }
  }

  createSupplier(): void {
    if (this.supplierForm.invalid) {
      Object.keys(this.supplierForm.controls).forEach(field => {
        this.supplierForm.get(field)?.markAsTouched({ onlySelf: true });
      });
      return;
    }

    const newSupplier: SupplierDTO = {
      supplierID: 0,
      companyName: this.supplierForm.value.companyName,
      primaryContactPerson: this.supplierForm.value.primaryContactPerson,
      primaryEmail: this.supplierForm.value.primaryEmail,
      primaryPhone: this.supplierForm.value.primaryPhone,
      website: this.supplierForm.value.website,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    const newLocation: Partial<SupplierLocationDTO> = {
      branchName: this.supplierForm.value.branchName,
      addressLine1: this.supplierForm.value.addressLine1,
      addressLine2: this.supplierForm.value.addressLine2,
      city: this.supplierForm.value.city,
      region: this.supplierForm.value.region,
      postalCode: this.supplierForm.value.postalCode,
      country: this.supplierForm.value.country,
      locationContactPerson: this.supplierForm.value.locationContactPerson,
      locationEmail: this.supplierForm.value.locationEmail,
      locationPhone: this.supplierForm.value.locationPhone,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    this.inventoryDataService.createSupplier(newSupplier, newLocation).subscribe({
      next: (response) => {
        const supplierWithLocation = { ...response.supplier, locations: [response.location] };
        this.selectedSupplier = supplierWithLocation;
        this.isCreatingSupplier = false;
        this.supplierForm.reset();
        this.suppliers.push(supplierWithLocation);
        this.toast.success('Supplier and location created successfully', 3000);
      },
      error: () => {
        this.toast.error('Failed to create supplier or location. Please try again.');
      },
    });
  }

  createCategory(): void {
    if (this.categoryForm.invalid) return;
    const newCategory: CategoryDTO = { categoryId: 0, categoryName: this.categoryForm.value.categoryName, createdAt: new Date() };
    this.inventoryDataService.createCategory(newCategory).subscribe({
      next: (response) => {
        this.selectedCategory = response;
        this.onCategorySelected(response);
        this.isCreatingCategory = false;
        this.categoryForm.reset();
        this.categories.push(response);
      },
      error: (err) => console.error('Error creating category:', err),
    });
  }

  generateSKU(productName: string): string {
    return this.inventoryDataService.generateSKU(productName);
  }

  addItemPriceHistory(itemID: number, purchasePrice: number, purchaseQuantity: number): void {
    const currentUserEmail = this.authService.getCurrentUserEmail();
    if (!this.selectedSupplier || !this.selectedSupplier.locations || this.selectedSupplier.locations.length === 0) {
      this.showAlert('Error', 'Supplier location is missing, cannot save price history.', 'bg-danger');
      return;
    }

    const priceHistoryEntry: ItemSupplierPriceHistoryDTO = {
      itemSupplierPriceID: 0,
      itemID,
      supplierLocationID: this.selectedSupplier.locations[0].locationID,
      purchasePrice,
      purchaseQuantity,
      currencyCode: 'ZAR',
      purchaseUOMID: this.inventoryForm.value.PurchaseUOMID,
      uomConversionFactor: 1,
      supplierItemCode: this.inventoryForm.value.itemCode,
      leadTimeDays: 0,
      validFrom: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      createdBy: currentUserEmail || 'System',
    };

    const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` });
    this.http.post(`${environment.apiUrl}/api/ItemSupplierPriceHistory/Add`, priceHistoryEntry, { headers }).subscribe({
      next: () => this.showAlert('Success', 'Price history updated.', 'bg-success'),
      error: () => this.showAlert('Error', 'Failed to update price history.', 'bg-danger'),
    });
  }

  saveItem(): void {
    if (this.isAddingQuantity) {
      this.addQuantityToItem();
      return;
    }

    const formValue = this.inventoryForm.getRawValue();

    if (this.isEditing && this.selectedItem) {
      const oldPrice = this.selectedItem.purchasePrice;
      const newPrice = formValue.purchasePrice;
      if (oldPrice !== newPrice) {
        this.addItemPriceHistory(this.selectedItem.itemID, newPrice, formValue.quantityToAdd || 1);
      }
    }

    if (!formValue.categoryID) {
      this.showAlert('Validation Error', 'Please select a category');
      return;
    }
    if (!formValue.PurchaseUOMID) {
      this.showAlert('Validation Error', 'Please select a unit of measure');
      return;
    }
    if (formValue.lowStockThreshold === null || formValue.lowStockThreshold === undefined || formValue.lowStockThreshold === '') {
      this.showAlert('Validation Error', 'Please enter a reorder level for raw materials');
      return;
    }
    if (!this.selectedSupplier) {
      this.showAlert('Validation Error', 'Please select a supplier for raw materials');
      return;
    }

    const itemData: any = {
      ItemID: this.isEditing && this.selectedItem ? this.selectedItem.itemID : 0,
      ItemName: formValue.itemName,
      ItemCode: formValue.itemCode || this.generateSKU(formValue.itemName),
      itemTypeID: 1,
      PurchaseUOMID: formValue.PurchaseUOMID,
      lowStockThreshold: formValue.lowStockThreshold ?? 0,
      isActive: this.isEditing && this.selectedItem ? (this.selectedItem.isActive ?? true) : true,
      valuationMethodCode: formValue.valuationMethodCode || 'FIFO',
      categoryID: formValue.categoryID,
      // This screen has no price field, and Update saves the whole body: an edit sends the
      // stored values so a Buy Out keeps the price and markup set in the Cost Calculator.
      profitMarginPercent: this.isEditing && this.selectedItem ? this.selectedItem.profitMarginPercent : (formValue.profitMarginPercent || 0),
      sellingPrice: this.isEditing && this.selectedItem ? this.selectedItem.sellingPrice : 0,
      supplierID: this.selectedSupplier.supplierID,
      isBuyout: !!formValue.isBuyout,
    };

    let hasActualChanges = false;
    if (!this.isEditing) {
      itemData.purchasePrice = formValue.purchasePrice || 0;
      itemData.unitCost = formValue.unitCost || 0;
      itemData.purchaseQuantity = formValue.quantityToAdd || 1;
      itemData.quantity = formValue.quantityToAdd || 0;
      hasActualChanges = true;
    } else if (this.isEditing && this.showPurchaseFields && this.selectedItem) {
      const originalPrice = this.selectedItem.purchasePrice || 0;
      const originalQuantity = this.selectedItem.quantity || 0;
      const newPrice = formValue.purchasePrice || 0;
      const newQuantity = formValue.quantityToAdd || 0;
      const priceChanged = Math.abs(newPrice - originalPrice) > 0.01;
      const quantityChanged = newQuantity !== originalQuantity && newQuantity > 0;

      if (priceChanged || quantityChanged) {
        itemData.purchasePrice = newPrice;
        itemData.unitCost = formValue.unitCost || 0;
        itemData.purchaseQuantity = newQuantity;
        itemData.quantity = newQuantity;
        hasActualChanges = true;
      } else {
        itemData.purchasePrice = this.selectedItem.purchasePrice || 0;
        itemData.unitCost = this.selectedItem.unitCost || 0;
        itemData.purchaseQuantity = this.selectedItem.purchaseQuantity || 1;
        itemData.quantity = this.selectedItem.quantity || 0;
      }
    } else if (this.isEditing && !this.showPurchaseFields && this.selectedItem) {
      itemData.purchasePrice = this.selectedItem.purchasePrice || 0;
      itemData.unitCost = this.selectedItem.unitCost || 0;
      itemData.purchaseQuantity = this.selectedItem.purchaseQuantity || 1;
      itemData.quantity = this.selectedItem.quantity || 0;
    }

    (itemData as any).hasActualChanges = hasActualChanges;

    const apiUrl = this.isEditing && this.selectedItem
      ? `${environment.apiUrl}/api/Items/Update?ItemId=${this.selectedItem.itemID}`
      : `${environment.apiUrl}/api/Items/Add`;
    const httpMethod = this.isEditing ? 'put' : 'post';
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' });

    this.http.request<ItemDTO>(httpMethod, apiUrl, { body: itemData, headers, observe: 'response', responseType: 'json' }).pipe(
      switchMap((response: any) => {
        if (response.status >= 400) throw new Error(`Server returned status ${response.status}`);
        const itemResponse = response.body;
        if (!itemResponse) throw new Error('No response body received from server');
        const savedItemId = this.isEditing && this.selectedItem ? this.selectedItem.itemID : itemResponse.itemID;

        if ((itemData as any).hasActualChanges) {
          const inventoryTransaction: InventoryTransactionDTO = {
            itemID: savedItemId,
            warehouseID: 1,
            transactionType: this.isEditing ? 'Adjustment' : 'Receipt',
            quantityChange: formValue.quantityToAdd || 0,
            costPerUnit: formValue.unitCost || 0,
            lowStockThreshold: formValue.lowStockThreshold !== undefined ? formValue.lowStockThreshold : 0,
            transactionDate: new Date(),
            referenceType: 'Manual Entry',
            referenceID: `UI-${new Date().getTime()}`,
            userID: 1,
            notes: `${this.isEditing ? 'Updated' : 'Added'} via Inventory Dashboard`,
            batchNumber: null,
            expiryDate: null,
          };
          const txHeaders = new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('authToken')}`);
          return this.http.post(`${environment.apiUrl}/api/InventoryTransactions/Add`, inventoryTransaction, { headers: txHeaders }).pipe(
            map(() => ({ itemId: savedItemId }))
          );
        }
        return of({ itemId: savedItemId });
      })
    ).subscribe({
      next: () => {
        this.loadItems();
        this.showAddModal = false;
        this.inventoryForm.reset();
        this.showAlert('Success', `Item ${this.isEditing ? 'updated' : 'added'} successfully`, '#4de270');
      },
      error: (error) => {
        if (error.error) {
          try {
            const errorObj = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;
            this.errorMessage = errorObj.title || 'Failed to save item. Please try again.';
            if (errorObj.errors) {
              this.errorMessage = Object.entries(errorObj.errors)
                .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
                .join('\n') || this.errorMessage;
            }
          } catch {
            this.errorMessage = 'Failed to save item. Please try again.';
          }
        } else {
          this.errorMessage = 'Failed to save item. Please try again.';
        }
        this.showAlert('Error', this.errorMessage || 'Failed to save item. Please try again.', 'bg-danger');
      },
    });
  }

  deleteItem(item: EnhancedItemDTO): void {
    const token = localStorage.getItem('authToken');
    if (!token) {
      this.toast.error('Session expired. Please log in again.', 3000);
      setTimeout(() => { this.authService.logOut(); }, 3000);
      return;
    }

    const headers = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` });
    const updatedItem = { ...item, isActive: false };

    this.http.put(`${environment.apiUrl}/api/Items/Update?ItemId=${item.itemID}`, updatedItem, { headers }).pipe(
      catchError(error => {
        let msg = 'Failed to remove item. Please try again.';
        if (error.status === 404) msg = 'Item not found.';
        else if (error.status === 401) msg = 'Unauthorized. Please log in again.';
        else if (error.status === 409) msg = 'Cannot remove item as it is referenced by other records.';
        this.toast.error(msg, 3000);
        throw error;
      })
    ).subscribe({
      next: () => {
        this.rawMaterials = this.rawMaterials.filter(rm => rm.itemID !== item.itemID);
        this.filteredRawMaterials = this.filteredRawMaterials.filter(rm => rm.itemID !== item.itemID);
        this.calculateStatistics();
        this.prepareChartsData();
        this.loadrawmattotal();
        this.showAddModal = false;
        this.selectedItem = null;
        this.selectedSupplier = null;
        this.selectedCategory = null;
        this.inventoryForm.reset();
        this.showAlert('Success', `Item ${item.itemName} deactivated successfully`, 'bg-success');
      },
    });

    this.loadItems();
    setTimeout(() => {
      this.loadrawmattotal();
      this.calculateStatistics();
    }, 500);
  }

  showAlert(title: string, message: string, bgColor: string = 'bg-danger', textColor: string = 'text-white'): void {
    this.alertTitle = title;
    this.alertMessage = message;
    this.alertBgColor = bgColor;
    this.alerttextcolor = textColor;
    this.isAlertVisible = true;
    setTimeout(() => { this.isAlertVisible = false; }, 5000);
  }

  navigateToAddSupplier(): void {
    this.router.navigate(['/config'], { queryParams: { tab: 'suppliers', view: 'single' }, state: { from: 'inventory' } });
  }

  // ── Import / Export ──────────────────────────────────────────────────────
  async downloadTemplate(): Promise<void> {
    try {
      const categories = this.categories.map(c => c.categoryName);
      const uoms = this.uomList.map(u => u.uomCode);
      const suppliers = this.suppliers.map(s => s.companyName);
      await this.excelService.generateInventoryTemplate(categories, uoms, suppliers);
    } catch {
      this.showAlert('Error', 'Failed to generate template. Please try again.', 'bg-danger');
    }
  }

  errorCount(): number {
    return this.inventoryImportService.errorCount(this.importResults?.errors || []);
  }

  warningCount(): number {
    return this.inventoryImportService.warningCount(this.importResults?.errors || []);
  }

  hasRawMaterialErrors(): boolean {
    return this.inventoryImportService.hasRawMaterialErrors(this.importResults?.errors || [], this.itemTypes);
  }

  async downloadFailedRawMaterials(): Promise<void> {
    try {
      const { exported } = await this.inventoryImportService.downloadFailedRawMaterials(
        this.importResults?.errors || [],
        { categories: this.categories, uomList: this.uomList, suppliers: this.suppliers, itemTypes: this.itemTypes }
      );
      if (!exported) {
        this.showAlert('No Raw Materials', 'There are no failed raw material rows to export.', 'bg-warning');
      }
    } catch {
      this.showAlert('Error', 'Failed to export failed rows. Please try again.', 'bg-danger');
    }
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    this.processImportFile(file);
  }

  private buildImportContext(): ImportContext {
    const existingItems: any[] = [...(this.rawMaterials || []), ...(this.originalRawMaterials || [])];
    return {
      categories: this.categories,
      suppliers: this.suppliers,
      uomList: this.uomList,
      itemTypes: this.itemTypes,
      existingItems,
    };
  }

  async processImportFile(file: File): Promise<void> {
    this.isImporting = true;
    this.importProgress = 0;
    this.importResults = { success: 0, errors: [] };

    try {
      const context = this.buildImportContext();
      const result = await this.inventoryImportService.processImportFile(file, context, (progress) => { this.importProgress = progress; });
      this.importResults.success = result.success;
      this.importResults.errors = result.errors;

      if (!result.validItemsFound) {
        setTimeout(() => {
          this.isImporting = false;
          const errorModalElement = document.getElementById('errorDetailsModalRaw');
          if (errorModalElement) new bootstrap.Modal(errorModalElement).show();
        }, 300);
        return;
      }

      this.showAlert('Success', 'Imported ' + this.importResults.success + ' items successfully', 'bg-success');
      setTimeout(() => {
        this.isImporting = false;
        if (this.importResults.errors.length > 0) {
          const errorModalElement = document.getElementById('errorDetailsModalRaw');
          if (errorModalElement) new bootstrap.Modal(errorModalElement).show();
        }
        this.loadItems();
      }, 500);
    } catch {
      this.isImporting = false;
      this.showAlert('Error', 'Failed to process import file. Please check the format and try again.', 'bg-danger');
    } finally {
      if (this.fileInput) {
        this.fileInput.nativeElement.value = '';
      }
    }
  }

  // ── Mobile ────────────────────────────────────────────────────────────────
  // The mobile layout is the same feature as the desktop one, so it is hosted
  // by this page too (spec §1 splits the screen, not just its desktop view).
  // These handlers back the <app-mobile-inventory-view> outputs.

  saveItemMobile(payload: any): void {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (!token) { this.toast.error('Session expired. Please log in again.', 3000); return; }
    const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' });

    // "Add quantity to an existing item" writes a stock movement, not an item.
    if (payload.isAddingQuantity && payload.selectedItem) {
      const item = payload.selectedItem;
      const tx = {
        ItemID: item.itemID,
        WarehouseID: 1,
        TransactionType: 'Receipt',
        QuantityChange: Number(payload.itemData.quantityToAdd ?? 1),
        CostPerUnit: item.purchaseQuantity > 0 ? item.purchasePrice / item.purchaseQuantity : item.purchasePrice,
        TransactionDate: new Date().toISOString(),
        ReferenceType: 'Manual',
        ReferenceID: `ADD-${Date.now()}`,
        UserID: 1,
        Notes: 'Quantity added via Mobile',
        BatchNumber: null,
        ExpiryDate: null,
        ReferenceTypeCode: 'MANUAL',
      };
      this.http.post(`${environment.apiUrl}/api/InventoryTransactions/Add`, tx, { headers }).subscribe({
        next: () => { this.loadItems(); this.toast.success('Quantity added successfully', 3000); },
        error: () => this.toast.error('Failed to add quantity. Please try again.', 3000),
      });
      return;
    }

    const { itemData, isEditing, itemID } = payload;
    const stored = isEditing && itemID ? this.originalRawMaterials.find(i => i.itemID === itemID) : undefined;
    const body: any = {
      ItemID: isEditing && itemID ? itemID : 0,
      ItemName: itemData.itemName,
      ItemCode: itemData.itemCode || this.generateSKU(itemData.itemName),
      itemTypeID: 1,
      PurchaseUOMID: itemData.purchaseUOMID,
      lowStockThreshold: itemData.lowStockThreshold ?? 0,
      isActive: itemData.isActive ?? true,
      valuationMethodCode: 'FIFO',
      categoryID: itemData.categoryID,
      profitMarginPercent: stored ? stored.profitMarginPercent : 0,
      sellingPrice: stored ? stored.sellingPrice : 0,
      purchasePrice: itemData.purchasePrice ?? 0,
      purchaseQuantity: itemData.purchaseQuantity ?? 1,
      quantity: itemData.quantity ?? 0,
      unitCost: itemData.unitCost ?? 0,
      supplierID: itemData.supplierID ?? null,
      // Spec §4 — the backend creates/retires the self-referencing BOM off this flag.
      isBuyout: !!itemData.isBuyout,
    };

    const url = isEditing && itemID
      ? `${environment.apiUrl}/api/Items/Update?ItemId=${itemID}`
      : `${environment.apiUrl}/api/Items/Add`;
    const method = isEditing ? 'put' : 'post';

    this.http.request<any>(method, url, { body, headers, observe: 'response' }).pipe(
      switchMap((resp: any) => {
        if (resp.status >= 400) throw new Error(`Server error ${resp.status}`);
        const savedId = isEditing && itemID ? itemID : resp.body?.itemID;
        if (!savedId) return of(null);
        // On edit, only move stock when the purchase price/qty actually changed.
        const shouldPostTx = isEditing
          ? (itemData.hasPurchaseChange === true && (itemData.purchaseQuantity ?? 0) > 0)
          : true;
        if (!shouldPostTx) return of(savedId);
        const tx: InventoryTransactionDTO = {
          itemID: savedId,
          warehouseID: 1,
          transactionType: isEditing ? 'Adjustment' : 'Receipt',
          quantityChange: isEditing ? (itemData.purchaseQuantity ?? 0) : (itemData.quantity ?? 0),
          costPerUnit: itemData.unitCost ?? 0,
          lowStockThreshold: itemData.lowStockThreshold ?? 0,
          transactionDate: new Date(),
          referenceType: 'Manual Entry',
          referenceID: `UI-${Date.now()}`,
          userID: 1,
          notes: `${isEditing ? 'Updated' : 'Added'} via Mobile`,
          batchNumber: null,
          expiryDate: null,
        };
        return this.http.post(`${environment.apiUrl}/api/InventoryTransactions/Add`, tx, { headers }).pipe(
          map(() => savedId),
          catchError(() => of(savedId))
        );
      }),
      catchError(() => {
        this.toast.error('Failed to save item. Please try again.', 3000);
        return of(null);
      })
    ).subscribe(savedId => {
      if (savedId !== null) {
        this.loadItems();
        this.toast.success(`Item ${isEditing ? 'updated' : 'added'} successfully`, 3000);
      }
    });
  }

  deleteItemMobile(payload: { item: EnhancedItemDTO }): void {
    this.deleteItem(payload.item);
  }

  createCategoryMobile(name: string): void {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    const newCategory: CategoryDTO = { categoryId: 0, categoryName: trimmed, createdAt: new Date() };
    this.inventoryDataService.createCategory(newCategory).subscribe({
      next: (response) => {
        this.categories.push(response);
        this.toast.success('Category created', 3000);
      },
      error: () => this.toast.error('Failed to create category.', 3000),
    });
  }
}
